import { forwardRef } from 'react';
import { cn } from '../../lib/utils.js';
import { Container } from './Container.jsx';

const sectionVariants = {
  default: 'bg-white',
  light: 'bg-secondary',
  dark: 'bg-dark-1 text-white',
  accent: 'bg-accent text-white',
  transparent: 'bg-transparent',
};

const sectionPaddings = {
  none: '',
  sm: 'py-section-sm',
  md: 'py-section-md',
  lg: 'py-section-lg',
};

/**
 * Section component with consistent spacing and variants
 */
export const Section = forwardRef(function Section(
  {
    children,
    variant = 'default',
    padding = 'lg',
    container = true,
    containerSize = 'default',
    className,
    id,
    as: Component = 'section',
    ...props
  },
  ref
) {
  const content = container ? (
    <Container size={containerSize}>{children}</Container>
  ) : (
    children
  );

  return (
    <Component
      ref={ref}
      id={id}
      className={cn(
        sectionVariants[variant],
        sectionPaddings[padding],
        className
      )}
      {...props}
    >
      {content}
    </Component>
  );
});

/**
 * Section Header component
 */
export function SectionHeader({
  title,
  subtitle,
  description,
  alignment = 'center',
  className,
}) {
  const alignments = {
    left: 'text-left',
    center: 'text-center mx-auto',
    right: 'text-right ml-auto',
  };

  return (
    <div className={cn('max-w-2xl mb-12 md:mb-16', alignments[alignment], className)}>
      {subtitle && (
        <span className="block text-md uppercase tracking-widest text-accent mb-4">
          {subtitle}
        </span>
      )}
      {title && (
        <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl mb-4 text-gray-900 dark:text-gray-100">
          {title}
        </h2>
      )}
      {description && (
        <p className="text-gray-600 dark:text-gray-300 text-lg">{description}</p>
      )}
    </div>
  );
}

// Attach sub-components
Section.Header = SectionHeader;

export default Section;
